import { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api'

/**
 * DECLARED HERE RATHER THAN IN `lib/types.ts`, exactly as `useOutings`,
 * `useMemberProfile` and `useRoundHistory` declare theirs. That file is copied
 * VERBATIM into the Caddie app by `bin/mobile contract`, so a shape put there
 * lands in the app's source tree whether the app wants it or not. A standing
 * order is a web club surface; Caddie has no notion of one and imports none of
 * these.
 */
/* ------------------------------------------------------------------ *
 * Standing tee times — a member's recurring order.
 *
 * A standing order NEVER spends. It claims a matching tee as a `proposed`
 * outing and tells its owner; the reservation still goes through
 * POST /golf/outings/:id/book, where a human types the card. There is
 * deliberately no field here that could change that.
 * ------------------------------------------------------------------ */

export type StandingTeeTimeStatus = 'active' | 'paused'

/**
 * What one run of a standing order did about one date.
 *
 *   claimed           a proposal exists; the member still has to confirm it
 *   booked            a reservation was made (unreachable in this build)
 *   no_tee            the sheet opened and held nothing inside the window
 *   over_ceiling      a tee matched and cost more than the member allows
 *   refused           a booking guard said no
 *   awaiting_member   the member has unconfirmed proposals already
 *   sheet_unreadable  the sheet could not be read; the date is retried
 */
export type StandingAttemptOutcome =
  | 'claimed'
  | 'booked'
  | 'no_tee'
  | 'over_ceiling'
  | 'refused'
  | 'awaiting_member'
  | 'sheet_unreadable'

export interface StandingTeeTimeAttempt {
  id: string
  /** The COURSE-LOCAL date, YYYY-MM-DD — never a timestamp. */
  target_date: string
  outcome: StandingAttemptOutcome
  detail: string | null
  course_key: string | null
  course_name: string | null
  slot_tee_off_at: string | null
  price_cents_per_head: number | null
  /** Present on a claim, so the desk can link the row to the outing it made. */
  outing_id: string | null
  outing_status: OutingStatus | null
}

export interface StandingTeeTime {
  id: string
  /** 0 = Sunday … 6 = Saturday, on the course's clock. */
  weekday: number
  /** Minutes after local midnight — 450 is 7:30 AM. */
  target_minutes: number
  /** The same target, formatted server-side so no client re-derives a zone. */
  target_clock: string
  tolerance_minutes: number
  course_keys: string[]
  course_names: string[]
  slots: number
  holes: number
  /** The member's own ceiling. Required — there is no "no limit". */
  max_price_cents_per_head: number
  /**
   * How many reservations this order may COMMIT unattended. Always 0 today,
   * reported so a member can see the gate is shut, and never accepted from a
   * client.
   */
  auto_book_allowance: number
  status: StandingTeeTimeStatus
  paused_reason: string | null
  consecutive_misses: number
  attempts: StandingTeeTimeAttempt[]
}

export interface StandingTeeTimesResponse {
  standing_tee_times: StandingTeeTime[]
  /** Whether the runner is switched on in this environment at all. */
  enabled: boolean
}

export interface StandingTeeTimeResponse {
  standing_tee_time: StandingTeeTime
}

/** The shape a member may set. Status moves through pause/resume, not here. */
export interface StandingTeeTimeBody {
  weekday: number
  target_minutes: number
  tolerance_minutes: number
  course_keys: string[]
  slots: number
  holes: number
  max_price_cents_per_head: number
}

/**
 * The signed-in member's standing tee-time orders (GET /golf/standing_tee_times)
 * and every mutation the desk offers: set, pause, resume, delete.
 *
 * `enabled` here is the HOOK's own gate (is the viewer signed in). The
 * SERVER's `enabled` — whether the runner is switched on in this environment
 * at all — comes back in the payload and is surfaced separately, because an
 * order that exists but is never run is exactly the thing a member must not be
 * left to discover on their own.
 *
 * Deliberately thin, like useOutings: the server owns status, the miss counter
 * and the log, and every mutation swaps in the server's own copy of the rule
 * rather than patching a local guess.
 */
export function useStandingTeeTimes(enabled: boolean) {
  const [rules, setRules] = useState<StandingTeeTime[]>([])
  const [runnerEnabled, setRunnerEnabled] = useState(false)
  const [isLoading, setIsLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!enabled) return
    try {
      const data = await api.get<StandingTeeTimesResponse>('/golf/standing_tee_times')
      setRules(data.standing_tee_times)
      setRunnerEnabled(data.enabled)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your standing tee times.')
    } finally {
      setIsLoading(false)
    }
  }, [enabled])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetch-on-mount, the same accepted pattern as every house data hook (e.g. useOutings)
    void refresh()
  }, [refresh])

  /** Swap in the server's copy of one rule, wherever it sits in the list. */
  const replace = useCallback((rule: StandingTeeTime) => {
    setRules((prev) => prev.map((r) => (r.id === rule.id ? rule : r)))
  }, [])

  const create = useCallback(async (body: StandingTeeTimeBody): Promise<StandingTeeTime> => {
    const data = await api.post<StandingTeeTimeResponse>('/golf/standing_tee_times', {
      standing_tee_time: body,
    })
    setRules((prev) => [...prev, data.standing_tee_time])
    return data.standing_tee_time
  }, [])

  /**
   * The member's own kill switch. Its own call rather than an update carrying a
   * status, so "stop this now" can never be confused with an edit.
   */
  const pause = useCallback(
    async (id: string) => {
      const data = await api.post<StandingTeeTimeResponse>(`/golf/standing_tee_times/${id}/pause`, {})
      replace(data.standing_tee_time)
    },
    [replace],
  )

  const resume = useCallback(
    async (id: string) => {
      const data = await api.post<StandingTeeTimeResponse>(`/golf/standing_tee_times/${id}/resume`, {})
      replace(data.standing_tee_time)
    },
    [replace],
  )

  /** Deletes the order and its log. Outings it already proposed are untouched. */
  const destroy = useCallback(async (id: string) => {
    await api.delete(`/golf/standing_tee_times/${id}`)
    setRules((prev) => prev.filter((r) => r.id !== id))
  }, [])

  return {
    rules,
    runnerEnabled,
    isLoading: enabled ? isLoading : false,
    error,
    refresh,
    create,
    pause,
    resume,
    destroy,
  }
}
